import React, { useState, useMemo } from 'react';
import { LoadingButton, Checkbox } from '@one-thd/sui-atomic-components';
import { SweepstakesMarkdown } from '../../../utils/SweepstakesMarkdown';
import { windowExists } from '../../../../../common/utils/AccountUtils';
import { useFetchChallengesAccept } from '../../../../../hooks';
import { RenderGenericError } from '../../SweepstakesUtils';
import { ENDPOINTS } from '../../../../../core/Constants';

// Render components
const RenderCheckbox = ({ checked, onChange, text, testId }) => (
  <div className="sui-flex sui-items-center">
    <div className="sui-mr-2 sui-mt-1">
      <Checkbox
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        data-testid={testId}
      />
    </div>
    <SweepstakesMarkdown markdownText={text} disableSpacing />
  </div>
);

const RenderFooterContent = ({
  acceptError,
  isLoadingAccept,
  isButtonDisabled,
  handleTakeSurvey,
  ctaTitle,
}) => (
  <div className="sui-space-y-2">
    {acceptError && (
      <div className="sui-py-3">
        <RenderGenericError
          refresh={handleTakeSurvey}
          testId="sweepstakes-accept-error"
        />
      </div>
    )}
    <LoadingButton
      variant="primary"
      fullWidth
      disabled={isButtonDisabled}
      loading={isLoadingAccept}
      onClick={handleTakeSurvey}
      data-testid="opt-in-take-survey-button"
    >
      {ctaTitle}
    </LoadingButton>
  </div>
);

export const OptInContent = (props) => {
  const { sweepstakesData, forceAcceptSuccess, handleOptIn } = props || {};
  const {
    preOptInDrawerDescription,
    postOptInDrawerDescription,
    preOptInDrawerCta,
    optInCheckboxes = [],
    requiredCheckboxIndices = [],
    challengeId,
    customerChallengeId,
    isV2
  } = sweepstakesData || {};

  const [checkedStates, setCheckedStates] = useState({});

  const acceptChallengeInput = useMemo(() => ({
    id: challengeId,
    customerChallengeId,
    isV2
  }), [challengeId, customerChallengeId, isV2]);

  const { isLoadingAccept,
    hasErrorAccept,
    getAcceptChallenge } = useFetchChallengesAccept({ acceptChallengeInput });

  // Only validate required checkboxes (checkbox_1 is optional for email marketing)
  const areAllRequiredChecked = Array.isArray(requiredCheckboxIndices)
    ? requiredCheckboxIndices.every((index) => checkedStates[index])
    : optInCheckboxes?.every((_item, index) => checkedStates[index]);

  const handleCheckboxChange = (index, checked) => {
    setCheckedStates((prev) => ({
      ...prev,
      [index]: checked,
    }));
  };

  const handleTakeSurvey = async () => {
    if (areAllRequiredChecked) {
      let isAcceptSuccessful = false;

      if (forceAcceptSuccess) {
        // eslint-disable-next-line no-console
        console.log('OptInContent -- Bypassing acceptSweepstakes call! (for testing purposes)');
        isAcceptSuccessful = true;
      } else {
        try {
          const response = await getAcceptChallenge();
          isAcceptSuccessful = response?.data?.[ENDPOINTS.ACCEPT_CHALLENGES]
            ?.challenges?.[0]?.optedStatus === 'OPTED_IN';
        } catch (error) {
          console.error('Accept challenge failed:', error);
          isAcceptSuccessful = false;
        }
      }

      if (isAcceptSuccessful && windowExists()) {
        handleOptIn();
        sessionStorage.setItem('checkbox_communication_consent', Boolean(checkedStates?.['0']));
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.sweepstakes-accepted', {
          termsAccepted: true,
          associateConsent: true,
        });
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-survey-drawer', {});
      }
    }
  };

  const isButtonDisabled = !areAllRequiredChecked || hasErrorAccept;
  const description = postOptInDrawerDescription || preOptInDrawerDescription;

  const content = (
    <div className="sui-space-y-4" data-testid="sweepstakes-opt-in-content">
      <div className="sui-space-y-4">
        {description && <SweepstakesMarkdown markdownText={description} />}
      </div>
      <div className="sui-space-y-2 sui-pt-6">
        {optInCheckboxes.map((text, index) => (
          <RenderCheckbox
            key={`opt-in-checkbox-${index}`}
            checked={!!checkedStates[index]}
            onChange={(checked) => handleCheckboxChange(index, checked)}
            text={text}
            testId={`opt-in-checkbox-${index}`}
          />
        ))}
      </div>
    </div>
  );

  const footer = (
    <RenderFooterContent
      acceptError={hasErrorAccept}
      isLoadingAccept={isLoadingAccept}
      isButtonDisabled={isButtonDisabled}
      handleTakeSurvey={handleTakeSurvey}
      ctaTitle={preOptInDrawerCta || 'Take Survey'}
    />
  );

  return { content, footer };
};
